import dayjs from 'dayjs';
import { salesText, salesFormat } from '../../locale';
import { notificationDisplayCopy } from './notificationPresentation';

import type { SalesEventBusPayload } from '../../types';
import { registerBrowserNotifications } from '../../utils/browserNotifications';
import {
  canonicalSalesLeadUrl,
  canonicalSalesTabPath,
  navigateToCanonicalSalesLead,
} from '../../salesNavigation';

export { registerBrowserNotifications };

const NOTIFICATIONS_STORAGE_KEY = 'sales.notification_center.items.v1';
const PREFS_STORAGE_KEY = 'sales.notification_center.preferences.v1';
const UNREAD_CAP = 99;

export type NotificationCategory = 'tasks' | 'leads' | 'whatsapp' | 'requests' | 'system';

export type NotificationPriority = 'critical' | 'high' | 'normal' | 'low';

export type NotificationType =
  | 'new_lead_assigned'
  | 'lead_transfer_approved'
  | 'lead_transfer_rejected'
  | 'registration_link_used'
  | 'need_review_resolved'
  | 'hot_lead_no_activity'
  | 'lead_no_next_action'
  | 'followup_due_soon'
  | 'task_overdue'
  | 'task_snooze_reminder'
  | 'meeting_due_soon'
  | 'whatsapp_message_received'
  | 'transfer_request_status_changed'
  | 'need_review_response'
  | 'discount_request_status_changed'
  | 'lead_addition_request_submitted'
  | 'lead_addition_request_approved'
  | 'lead_addition_request_rejected'
  | 'lead_addition_request_returned'
  | 'notifications_permission_disabled'
  | 'sync_issue'
  | 'import_issue';

export interface SalesNotification {
  id: string;
  employee_id: number;
  category: NotificationCategory;
  type: NotificationType;
  priority: NotificationPriority;
  title: string;
  description: string;
  related_lead_id?: number | null;
  related_task_id?: string | null;
  related_email_id?: number | null;
  related_request_id?: string | null;
  action_url?: string | null;
  action_payload?: Record<string, unknown> | null;
  is_read: boolean;
  created_at: string;
  expires_at?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface NotificationPreferences {
  enableBrowserNotifications: boolean;
  followupReminders: boolean;
  newLeadAssigned: boolean;
  whatsappMessages: boolean;
  transferRequestUpdates: boolean;
  needReviewUpdates: boolean;
}

export interface NotificationFilters {
  tab: 'all' | 'unread' | 'tasks' | 'leads' | 'whatsapp' | 'requests';
}

export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  enableBrowserNotifications: false,
  followupReminders: true,
  newLeadAssigned: true,
  whatsappMessages: true,
  transferRequestUpdates: true,
  needReviewUpdates: true,
};

const PRIORITY_SCORE: Record<NotificationPriority, number> = {
  critical: 4,
  high: 3,
  normal: 2,
  low: 1,
};

const safeParse = <T>(raw: string | null, fallback: T): T => {
  if (!raw) {
    return fallback;
  }
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

const readStoredNotifications = (): SalesNotification[] => {
  if (typeof window === 'undefined') {
    return [];
  }
  return safeParse<SalesNotification[]>(window.localStorage.getItem(NOTIFICATIONS_STORAGE_KEY), []);
};

const writeStoredNotifications = (rows: SalesNotification[]): void => {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.setItem(NOTIFICATIONS_STORAGE_KEY, JSON.stringify(rows.slice(0, 200)));
};

const sortNotifications = (rows: SalesNotification[]): SalesNotification[] =>
  [...rows].sort((a, b) => {
    const priorityDiff = PRIORITY_SCORE[b.priority] - PRIORITY_SCORE[a.priority];
    if (priorityDiff !== 0) {
      return priorityDiff;
    }
    return dayjs(b.created_at).valueOf() - dayjs(a.created_at).valueOf();
  });

export const getStoredNotificationsForEmployee = (employeeId: number): SalesNotification[] =>
  sortNotifications(readStoredNotifications().filter((item) => item.employee_id === employeeId && String(item.category) !== 'workmail' && item.metadata?.synthetic !== true));

export const getNotificationPreferences = async (): Promise<NotificationPreferences> => {
  if (typeof window === 'undefined') {
    return DEFAULT_NOTIFICATION_PREFERENCES;
  }
  return {
    ...DEFAULT_NOTIFICATION_PREFERENCES,
    ...safeParse<Partial<NotificationPreferences>>(window.localStorage.getItem(PREFS_STORAGE_KEY), {}),
  };
};

const textValue = (event: Record<string, unknown>, key: string): string => {
  const value = event[key];
  return typeof value === 'string' ? value.trim() : '';
};

const eventMetadata = (event: Record<string, unknown>): Record<string, unknown> => {
  const metadata = event.metadata;
  return metadata && typeof metadata === 'object' && !Array.isArray(metadata)
    ? metadata as Record<string, unknown>
    : {};
};

const metadataTextValue = (metadata: Record<string, unknown>, key: string): string => {
  const value = metadata[key];
  return typeof value === 'string' ? value.trim() : '';
};

const leadDisplayName = (event: Record<string, unknown>, leadId: number, hasLeadId: boolean): string => {
  return textValue(event, 'lead_name')
    || textValue(event, 'full_name')
    || (hasLeadId ? `Lead #${leadId}` : 'New lead');
};

const firstCourseInterest = (event: Record<string, unknown>): string => {
  const interests = event.course_interests;
  if (!Array.isArray(interests)) {
    return '';
  }
  for (const item of interests) {
    if (!item || typeof item !== 'object') {
      continue;
    }
    const course = (item as Record<string, unknown>).course;
    if (typeof course === 'string' && course.trim()) {
      return course.trim();
    }
  }
  return '';
};

const isSheetIngestEvent = (event: Record<string, unknown>): boolean => {
  const metadata = eventMetadata(event);
  if (metadataTextValue(metadata, 'sheet_name') || metadataTextValue(metadata, 'row_hash') || metadata.row) {
    return true;
  }
  return Boolean(textValue(event, 'campaign') && textValue(event, 'source').toLowerCase() === 'facebook');
};

const leadActionUrl = (leadId: number, hasLeadId: boolean): string | null =>
  hasLeadId
    ? canonicalSalesLeadUrl(
      leadId,
      typeof window === 'undefined'
        ? { pathname: '/sales/pipeline', search: '', hash: '' }
        : window.location,
    )
    : canonicalSalesTabPath('pipeline');

const sheetLeadDescription = (event: Record<string, unknown>, leadName: string): string => {
  const metadata = eventMetadata(event);
  const campaign = textValue(event, 'campaign') || metadataTextValue(metadata, 'sheet_name');
  const course = textValue(event, 'course') || firstCourseInterest(event) || metadataTextValue(metadata, 'assigned_course');
  const parts = [
    `${leadName} submitted a sales inquiry`,
    course ? `for ${course}` : '',
    campaign ? `from ${campaign}` : '',
  ].filter(Boolean);
  return `${parts.join(' ')}.`;
};

export const updateNotificationPreferences = async (prefs: NotificationPreferences): Promise<NotificationPreferences> => {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(PREFS_STORAGE_KEY, JSON.stringify(prefs));
  }
  return prefs;
};

const notificationFromEvent = (event: SalesEventBusPayload & Record<string, unknown>, employeeId: number): SalesNotification | null => {
  const type = String(event.canonical_type || event.type || '').toLowerCase();
  if (type === 'email_sent' || type === 'workmail_reply_received') return null;
  const rawTargetEmployeeId = event.target_employee_id;
  const hasSingularTarget = rawTargetEmployeeId !== null
    && rawTargetEmployeeId !== undefined
    && String(rawTargetEmployeeId).trim() !== '';
  if (hasSingularTarget) {
    const targetEmployeeId = Number(rawTargetEmployeeId);
    if (!Number.isSafeInteger(targetEmployeeId) || targetEmployeeId !== employeeId) return null;
  } else if (Array.isArray(event.target_employee_ids)) {
    const targetEmployeeIds = event.target_employee_ids
      .map(Number)
      .filter((id) => Number.isSafeInteger(id) && id > 0);
    if (!targetEmployeeIds.includes(employeeId)) return null;
  } else {
    return null;
  }
  const leadId = Number(event.lead_id);
  const hasLeadId = Number.isFinite(leadId) && leadId > 0;
  const createdAt = String(event.occurred_at || event.created_at || new Date().toISOString());

  if (type === 'lead_created' || (type === 'lead_updated' && isSheetIngestEvent(event))) {
    const leadName = leadDisplayName(event, leadId, hasLeadId);
    const isDuplicateSheetUpdate = type === 'lead_updated';
    return {
      id: `evt-${type}-${leadId}-${createdAt}`,
      employee_id: employeeId,
      category: 'leads',
      type: 'new_lead_assigned',
      priority: 'high',
      title: isDuplicateSheetUpdate ? 'Sheet lead updated' : 'New sheet lead',
      description: sheetLeadDescription(event, leadName),
      related_lead_id: hasLeadId ? leadId : null,
      related_task_id: null,
      related_email_id: null,
      related_request_id: null,
      action_url: leadActionUrl(leadId, hasLeadId),
      action_payload: { action: 'open_lead' },
      is_read: false,
      created_at: createdAt,
      metadata: {
        source_event: type,
        source: textValue(event, 'source') || null,
        campaign: textValue(event, 'campaign') || metadataTextValue(eventMetadata(event), 'sheet_name') || null,
      },
    };
  }

  if (type === 'lead_assigned' || type === 'lead_assigned_info') {
    return {
      id: `evt-${type}-${leadId}-${createdAt}`,
      employee_id: employeeId,
      category: 'leads',
      type: 'new_lead_assigned',
      priority: 'high',
      title: 'New lead assigned',
      description: `A new lead was assigned to you${hasLeadId ? ` (Lead #${leadId})` : ''}.`,
      related_lead_id: hasLeadId ? leadId : null,
      related_task_id: null,
      related_email_id: null,
      related_request_id: null,
      action_url: leadActionUrl(leadId, hasLeadId),
      action_payload: { action: 'open_lead' },
      is_read: false,
      created_at: createdAt,
      metadata: { source_event: type },
    };
  }

  if (type === 'registration_link_used' || type === 'registration_via_link') {
    const leadName = leadDisplayName(event, leadId, hasLeadId);
    const course = textValue(event, 'course_name') || textValue(event, 'course');
    const studentId = Number(event.student_id);
    return {
      id: `evt-${type}-${leadId}-${Number.isFinite(studentId) ? studentId : 'student'}-${createdAt}`,
      employee_id: employeeId,
      category: 'leads',
      type: 'registration_link_used',
      priority: 'high',
      title: 'Registration completed',
      description: `${leadName} registered from the sales link${course ? ` for ${course}` : ''}.`,
      related_lead_id: hasLeadId ? leadId : null,
      related_task_id: null,
      related_email_id: null,
      related_request_id: null,
      action_url: leadActionUrl(leadId, hasLeadId),
      action_payload: { action: 'open_lead' },
      is_read: false,
      created_at: createdAt,
      metadata: {
        source_event: type,
        student_id: Number.isFinite(studentId) ? studentId : null,
        course: course || null,
        course_interest_id: Number(event.course_interest_id) || null,
      },
    };
  }

  if (type === 'task_overdue') {
    return {
      id: `evt-${type}-${leadId}-${createdAt}`,
      employee_id: employeeId,
      category: 'tasks',
      type: 'task_overdue',
      priority: 'critical',
      title: 'Task overdue',
      description: 'A follow-up task is overdue and needs action now.',
      related_lead_id: hasLeadId ? leadId : null,
      related_task_id: event.related_task_id ? String(event.related_task_id) : null,
      related_email_id: null,
      related_request_id: null,
      action_payload: { action: 'open_task' },
      is_read: false,
      created_at: createdAt,
      metadata: { source_event: type },
    };
  }

  if (type === 'follow_up_created' || type === 'follow_up_reminder_triggered') {
    return {
      id: `evt-followup-${leadId}-${createdAt}`,
      employee_id: employeeId,
      category: 'tasks',
      type: 'followup_due_soon',
      priority: 'high',
      title: 'Follow-up due soon',
      description: 'A follow-up reminder is due soon.',
      related_lead_id: hasLeadId ? leadId : null,
      related_task_id: event.related_task_id ? String(event.related_task_id) : null,
      related_email_id: null,
      related_request_id: null,
      action_payload: { action: 'open_task' },
      is_read: false,
      created_at: createdAt,
      metadata: { source_event: type },
    };
  }

  if (type === 'need_review_resolved' || type === 'need_review_response') {
    return {
      id: `evt-review-${leadId}-${createdAt}`,
      employee_id: employeeId,
      category: 'requests',
      type: 'need_review_response',
      priority: 'high',
      title: 'Need review response',
      description: 'A review request has a new response.',
      related_lead_id: hasLeadId ? leadId : null,
      related_task_id: event.related_task_id ? String(event.related_task_id) : null,
      related_email_id: null,
      related_request_id: event.related_transfer_request_id ? String(event.related_transfer_request_id) : null,
      action_payload: { action: 'open_lead' },
      is_read: false,
      created_at: createdAt,
      metadata: { source_event: type },
    };
  }

  if (type === 'transfer_request_status_changed' || type === 'transfer_request_approved' || type === 'transfer_request_rejected') {
    return {
      id: `evt-transfer-${leadId}-${createdAt}`,
      employee_id: employeeId,
      category: 'requests',
      type: type.includes('rejected') ? 'lead_transfer_rejected' : 'lead_transfer_approved',
      priority: type.includes('rejected') ? 'high' : 'normal',
      title: type.includes('rejected') ? 'Transfer request rejected' : 'Transfer request approved',
      description: type.includes('rejected')
        ? 'Transfer request was rejected. Review reason and next step.'
        : 'Transfer request was approved.',
      related_lead_id: hasLeadId ? leadId : null,
      related_task_id: null,
      related_email_id: null,
      related_request_id: event.related_transfer_request_id ? String(event.related_transfer_request_id) : null,
      action_payload: { action: 'view_reason' },
      is_read: false,
      created_at: createdAt,
      metadata: { source_event: type },
    };
  }

  if (type === 'lead_addition_request_submitted' || type === 'lead_addition_request_decision') {
    const requestId = Number(event.request_id);
    const hasRequestId = Number.isFinite(requestId) && requestId > 0;
    const requestStatus = textValue(event, 'request_status').toLowerCase();
    const customerName = textValue(event, 'customer_name') || 'Customer';
    const requesterName = textValue(event, 'requested_by_name');
    const isSubmitted = type === 'lead_addition_request_submitted';
    if (isSubmitted && Number(event.actor_employee_id) === employeeId) {
      return null;
    }
    const notificationType: NotificationType = isSubmitted
      ? 'lead_addition_request_submitted'
      : requestStatus === 'approved'
        ? 'lead_addition_request_approved'
        : requestStatus === 'rejected'
          ? 'lead_addition_request_rejected'
          : 'lead_addition_request_returned';
    const title = isSubmitted
      ? 'New Lead request'
      : requestStatus === 'approved'
        ? 'New Lead request approved'
        : requestStatus === 'rejected'
          ? 'New Lead request rejected'
          : 'New Lead request returned for editing';
    const decisionNote = textValue(event, 'decision_note');
    const requestPhone = textValue(event, 'phone');
    const requestCourse = textValue(event, 'course');
    const description = isSubmitted
      ? `${requesterName || 'A sales employee'} requested adding ${customerName}${requestPhone ? ` (${requestPhone})` : ''}${requestCourse ? ` for ${requestCourse}` : ''}.`
      : `${customerName}: ${requestStatus.replaceAll('_', ' ')}${decisionNote ? ` - ${decisionNote}` : ''}.`;
    const requestUrl = isSubmitted
      ? `${canonicalSalesTabPath('lead_movement')}?tab=requests&type=new-leads${hasRequestId ? `&request=${requestId}` : ''}`
      : requestStatus === 'approved' && hasLeadId
        ? leadActionUrl(leadId, true)
        : `${canonicalSalesTabPath('pipeline')}?panel=create-lead${hasRequestId ? `&request=${requestId}` : ''}`;
    return {
      id: `evt-lead-addition-${hasRequestId ? requestId : createdAt}-${type}-${requestStatus || 'submitted'}`,
      employee_id: employeeId,
      category: 'requests',
      type: notificationType,
      priority: isSubmitted || requestStatus !== 'approved' ? 'high' : 'normal',
      title,
      description,
      related_lead_id: hasLeadId ? leadId : null,
      related_task_id: null,
      related_email_id: null,
      related_request_id: hasRequestId ? String(requestId) : null,
      action_url: requestUrl,
      action_payload: { action: 'open_lead_addition_request' },
      is_read: false,
      created_at: createdAt,
      metadata: { source_event: type, request_status: requestStatus || 'pending' },
    };
  }

  return null;
};

export const getNotifications = async (employeeId: number): Promise<SalesNotification[]> => {
  const stored = readStoredNotifications().filter((item) => item.metadata?.synthetic !== true);
  const byId = new Map<string, SalesNotification>();
  stored
    .filter((item) => item.employee_id === employeeId && String(item.category) !== 'workmail')
    .forEach((item) => {
      if (!byId.has(item.id)) {
        byId.set(item.id, item);
      }
    });

  const rows = sortNotifications(Array.from(byId.values()));

  writeStoredNotifications(stored);
  return rows;
};

export const addNotificationFromEvent = (event: SalesEventBusPayload, employeeId: number): SalesNotification | null => {
  const item = notificationFromEvent(event as SalesEventBusPayload & Record<string, unknown>, employeeId);
  if (!item) {
    return null;
  }
  const rows = readStoredNotifications();
  const exists = rows.some((row) => row.id === item.id);
  if (exists) {
    return item;
  }
  rows.unshift(item);
  writeStoredNotifications(rows);
  return item;
};

export const markNotificationRead = async (notificationId: string): Promise<void> => {
  const rows = readStoredNotifications().map((item) =>
    item.id === notificationId ? { ...item, is_read: true } : item,
  );
  writeStoredNotifications(rows);
};

export const dismissNotification = async (
  notificationId: string,
  options: { isCritical: boolean },
): Promise<void> => {
  if (options.isCritical) {
    return;
  }
  const rows = readStoredNotifications().filter((item) => item.id !== notificationId);
  writeStoredNotifications(rows);
};

export const getUnreadCount = (items: SalesNotification[]): number => {
  const unread = items.filter((item) => !item.is_read).length;
  return Math.min(unread, UNREAD_CAP);
};

export const getUnreadLabel = (items: SalesNotification[]): string => {
  const unread = items.filter((item) => !item.is_read).length;
  if (unread > UNREAD_CAP) {
    return '99+';
  }
  return String(unread);
};

export const filterNotifications = (items: SalesNotification[], filters: NotificationFilters): SalesNotification[] => {
  if (filters.tab === 'all') {
    return items;
  }
  if (filters.tab === 'unread') {
    return items.filter((item) => !item.is_read);
  }
  return items.filter((item) => item.category === filters.tab);
};

export const shouldSendBrowserNotification = (
  item: SalesNotification,
  preferences: NotificationPreferences,
): boolean => {
  if (!preferences.enableBrowserNotifications) {
    return false;
  }
  if (item.priority !== 'critical' && item.priority !== 'high') {
    return false;
  }
  if (item.type === 'followup_due_soon' && !preferences.followupReminders) {
    return false;
  }
  if (item.type === 'new_lead_assigned' && !preferences.newLeadAssigned) {
    return false;
  }
  if (item.type === 'registration_link_used' && !preferences.newLeadAssigned) {
    return false;
  }
  if (item.category === 'whatsapp' && !preferences.whatsappMessages) {
    return false;
  }
  if (item.category === 'requests' && !preferences.transferRequestUpdates && !preferences.needReviewUpdates) {
    return false;
  }
  return [
    'followup_due_soon',
    'meeting_due_soon',
    'task_overdue',
    'hot_lead_no_activity',
    'lead_no_next_action',
    'new_lead_assigned',
    'registration_link_used',
    'lead_transfer_rejected',
    'lead_transfer_approved',
    'lead_addition_request_submitted',
    'lead_addition_request_rejected',
    'lead_addition_request_returned',
  ].includes(item.type);
};

export const triggerBrowserNotification = (item: SalesNotification): void => {
  if (typeof window === 'undefined' || !('Notification' in window) || Notification.permission !== 'granted') {
    return;
  }
  const copy = notificationDisplayCopy(item, salesText, salesFormat);
  const body = copy.description || copy.title;
  const canonicalLeadUrl = item.related_lead_id
    ? canonicalSalesLeadUrl(item.related_lead_id, window.location)
    : null;
  const options: NotificationOptions & { renotify?: boolean; vibrate?: number[] } = {
    body,
    tag: `sales-notification-${item.id}`,
    renotify: false,
    requireInteraction: false,
    silent: false,
    data: {
      action: item.action_payload?.action,
      lead_id: item.related_lead_id ?? null,
      url: canonicalLeadUrl || item.action_url || null,
    },
  };
  const notification = new Notification(copy.title, options);
  notification.onclick = () => {
    window.focus();
    if (item.related_lead_id) {
      navigateToCanonicalSalesLead(item.related_lead_id);
    }
  };
};
